import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  IconButton,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useRef } from 'react';

const CategoriesSection = ({ categories, loading, selectedCategory, onSelectCategory }: any) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const amount = direction === 'left' ? -250 : 250;
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, overflow: 'hidden' }}>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} variant="rounded" width={160} height={120} sx={{ borderRadius: 3, flexShrink: 0 }} />
          ))}
        </Box>
      </Container>
    );
  }

  return (
    <Box id="categories-section" sx={{ bgcolor: 'background.paper', py: 4, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            تصفح حسب الفئة
          </Typography>
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton size="small" onClick={() => scroll('right')} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <ArrowForwardIos sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" onClick={() => scroll('left')} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <ArrowBackIosNew sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
        </Box>
        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            pt: 1,
            pb: 1,
            px: 1,
          }}
        >
          {/* All category button */}
          <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
            <Card
              onClick={() => onSelectCategory(null)}
              sx={{
                width: 140,
                minWidth: 140,
                maxWidth: 140,
                cursor: 'pointer',
                scrollSnapAlign: 'start',
                borderRadius: 3,
                border: '2px solid',
                borderColor: !selectedCategory ? 'primary.main' : 'divider',
                bgcolor: !selectedCategory ? 'primary.light' : 'background.paper',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: 'primary.light' },
                overflow: 'hidden',
              }}
            >
              <CardMedia
                component="img"
                image="/static/images/all.png"
                alt="الكل"
                sx={{ width: 140, height: 80, objectFit: 'contain' }}
              />
              <CardContent sx={{ textAlign: 'center', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color={!selectedCategory ? 'primary.contrastText' : 'text.primary'}
                >
                  الكل
                </Typography>
              </CardContent>
            </Card>
          </motion.div>

          {categories.map((category: any) => (
            <motion.div key={category.id} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Card
                onClick={() => onSelectCategory(category.id)}
                sx={{
                  width: 140,
                  minWidth: 140,
                  maxWidth: 140,
                  cursor: 'pointer',
                  scrollSnapAlign: 'start',
                  borderRadius: 3,
                  border: '2px solid',
                  borderColor: selectedCategory === category.id ? 'primary.main' : 'divider',
                  bgcolor: selectedCategory === category.id ? 'primary.light' : 'background.paper',
                  transition: 'all 0.2s ease',
                  '&:hover': { borderColor: 'primary.light' },
                  overflow: 'hidden',
                }}
              >
                {category.image && (
                  <CardMedia
                    component="img"
                    image={category.image}
                    alt={category.name}
                    sx={{ width: 140, height: 80, objectFit: 'contain' }}
                  />
                )}
                <CardContent sx={{ textAlign: 'center', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={selectedCategory === category.id ? 'primary.contrastText' : 'text.primary'}
                    noWrap
                  >
                    {category.name}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default CategoriesSection;
